import React from 'react';
import { ProgressBar, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faClipboardCheck, 
  faChartLine, 
  faTachometerAlt 
} from '@fortawesome/free-solid-svg-icons';
import { useWorkflow } from './WorkflowProvider';
import { useSearchParams } from 'react-router-dom';

interface StepInfo {
  id: number;
  title: string;
  icon: any;
  description: string;
}

const steps: StepInfo[] = [
  {
    id: 2,
    title: 'Comparable Analysis',
    icon: faClipboardCheck,
    description: 'Review comparable properties'
  },
  {
    id: 3,
    title: 'AI Valuation',
    icon: faChartLine,
    description: 'AI-powered price prediction'
  },
  {
    id: 4,
    title: 'Strategic Insights',
    icon: faTachometerAlt,
    description: 'Market strategy & pricing'
  }
];

const StepIndicator: React.FC = () => {
  const { state, setStep } = useWorkflow();
  const { currentStep, progress, currentOperation } = state;
  const [searchParams, setSearchParams] = useSearchParams();
  
  const handleStepClick = (stepId: number) => {
    const isCompleted = stepId < currentStep;
    const isAccessible = stepId <= currentStep;
    
    // Only allow navigation to completed steps or the current step
    if (isAccessible) {
      // Update the workflow state
      setStep(stepId as 1 | 2 | 3 | 4);
      
      // Update URL parameters
      const reportId = sessionStorage.getItem('currentReportId');
      if (reportId) {
        setSearchParams({ reportId, step: stepId.toString() });
      } else {
        setSearchParams({ step: stepId.toString() });
      }
    }
  };

  return (
    <Card className="mb-4">
      <Card.Body className="pb-2">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            {steps.find(s => s.id === currentStep)?.title || 'CMA Analysis'}
          </h5>
          <span className="text-muted">
            {Math.round(progress)}% Complete
          </span>
        </div>

        {/* Progress Bar */}
        <ProgressBar 
          now={progress} 
          className="mb-3"
          style={{ height: '8px' }}
          variant="success"
        />

        {/* Step Indicators */}
        <div className="d-flex justify-content-between align-items-start">
          {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            const isAccessible = step.id <= currentStep;

            return (
              <div 
                key={step.id}
                className={`text-center flex-grow-1 ${index < steps.length - 1 ? 'me-3' : ''}`}
                style={{ maxWidth: '200px' }}
              >
                {/* Step Circle */}
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2 ${
                    isCompleted 
                      ? 'bg-success text-white' 
                      : isActive 
                      ? 'bg-primary text-white' 
                      : 'bg-light text-muted'
                  } ${isAccessible ? 'cursor-pointer' : ''}`}
                  style={{ 
                    width: '48px', 
                    height: '48px',
                    border: isAccessible ? '2px solid' : '2px solid #e9ecef',
                    borderColor: isCompleted ? '#198754' : isActive ? '#0d6efd' : '#e9ecef',
                    cursor: isAccessible ? 'pointer' : 'default',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => handleStepClick(step.id)}
                  onMouseEnter={(e) => {
                    if (isAccessible) {
                      (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.1)';
                      (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                  }}
                  title={isAccessible ? `Go to ${step.title}` : `${step.title} (not available)`}
                >
                  <FontAwesomeIcon 
                    icon={step.icon} 
                    size="lg"
                  />
                </div>

                {/* Step Title */}
                <h6 
                  className={`mb-1 ${
                    isAccessible ? 'text-dark' : 'text-muted'
                  } ${isAccessible ? 'cursor-pointer' : ''}`}
                  style={{ 
                    fontSize: '0.9rem',
                    cursor: isAccessible ? 'pointer' : 'default',
                    transition: 'color 0.3s ease'
                  }}
                  onClick={() => handleStepClick(step.id)}
                  onMouseEnter={(e) => {
                    if (isAccessible) {
                      (e.currentTarget as HTMLElement).style.color = isActive ? '#0d6efd' : '#198754';
                      (e.currentTarget as HTMLElement).style.textDecoration = 'underline';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isAccessible) {
                      (e.currentTarget as HTMLElement).style.color = isAccessible ? '#212529' : '#6c757d';
                      (e.currentTarget as HTMLElement).style.textDecoration = 'none';
                    }
                  }}
                >
                  {step.title}
                </h6>

                {/* Step Description */}
                <small 
                  className={`d-block ${
                    isActive ? 'text-primary' : 'text-muted'
                  }`}
                  style={{ fontSize: '0.75rem' }}
                >
                  {step.description}
                </small>

                {/* Connection Line */}
                {index < steps.length - 1 && (
                  <div
                    className={`position-absolute ${
                      isCompleted ? 'bg-success' : 'bg-light'
                    }`}
                    style={{
                      top: '24px',
                      left: '50%',
                      right: '-50%',
                      height: '2px',
                      zIndex: -1,
                      transform: 'translateY(-50%)'
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Current Operation */}
        {currentOperation && (
          <div className="mt-3 p-2 bg-light rounded">
            <div className="d-flex align-items-center">
              <div 
                className="spinner-border spinner-border-sm text-primary me-2" 
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <small className="text-muted">
                {currentOperation}
              </small>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default StepIndicator; 